'use client';

import type { ComponentProps } from 'react';
import { createContext, useContext } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { Icon } from '@/app_components/common/Icon';
import * as Accordion from '@/app_components/ui/primitives/AccordionV2';
import { cn } from '@/app_lib/utils/utils';

// NOTE: This Accordion is meant to be used along with the Figma styles. You should *almost always* provide a variant.
// However, for ease, you can *not* include a variant and get the benefits of the component design.
const accordionStyles = tv({
  slots: {
    base: 'flex flex-col gap-xl',
    content: 'px-xs py-xxs',
    header: '', // Not sure what to do with this yet, so have fun with this whenever you need to use it
    item: 'group flex flex-col gap-4 divide-secondary',
    trigger: 'cursor-pointer w-full flex justify-between items-center',
  },
  variants: {
    variant: {
      boxed: {
        item: 'shadow-[0_4px_20px_0_rgba(0,0,0,0.04)] rounded-md data-[state=open]:divide-y',
        trigger: 'p-xs typography-level-5 hover:text-secondary font-medium',
      },
      default: {
        item: 'divide-y gap-xl',
        trigger: 'py-xs typography-level-5 hover:text-secondary font-medium',
      },
    },
  },
});

type AccordionVariants = VariantProps<typeof accordionStyles>;

const AccordionContext = createContext<(AccordionVariants & { type: 'single' | 'multiple' }) | null>(null);

const useAccordionContext = () => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error('Accordion components must be used within an AccordionBase');
  }
  return context;
};

interface AccordionProviderProps extends AccordionVariants {
  children: React.ReactNode;
  type: 'single' | 'multiple';
}

// TODO: Need to make sure this works properly on the server. Worried about a flash of unstyled content.
// Doesn't matter right now since variants don't do anything yet.
const AccordionProvider = ({ variant, children, type }: AccordionProviderProps) => {
  return <AccordionContext.Provider value={{ type, variant }}>{children}</AccordionContext.Provider>;
};

export const AccordionBase = ({
  className,
  variant,
  children,
  ...rest
}: {
  className?: string;
} & AccordionVariants &
  ComponentProps<typeof Accordion.Root>) => {
  return (
    <AccordionProvider
      type={rest.type}
      variant={variant}
    >
      <Accordion.Root
        className={cn(accordionStyles().base({ variant }), className)}
        {...rest}
      >
        {children}
      </Accordion.Root>
    </AccordionProvider>
  );
};
export const AccordionItem = ({
  className,
  children,
  ...rest
}: {
  className?: string;
} & ComponentProps<typeof Accordion.Item>) => {
  const { variant } = useAccordionContext();

  return (
    <Accordion.Item
      className={cn(accordionStyles().item({ variant }), className)}
      {...rest}
    >
      {children}
    </Accordion.Item>
  );
};

export const AccordionHeader = ({
  children,
  className,
  ...rest
}: {
  className?: string;
} & ComponentProps<typeof Accordion.Header>) => {
  const { variant } = useAccordionContext();

  return (
    <Accordion.Header
      className={cn(accordionStyles().header({ variant }), className)}
      {...rest}
    >
      {children}
    </Accordion.Header>
  );
};

// TODO: asChild does not work here since we need that icon
export const AccordionTrigger = ({
  children,
  className,
  ...rest
}: {
  className?: string;
} & ComponentProps<typeof Accordion.Trigger>) => {
  const { variant } = useAccordionContext();

  return (
    <Accordion.Trigger
      className={cn(accordionStyles().trigger({ variant }), className)}
      {...rest}
    >
      <span>{children}</span>
      <Icon
        className='group-data-[state=open]:rotate-180'
        icon='ChevronDown'
      />
    </Accordion.Trigger>
  );
};

export const AccordionContent = ({
  children,
  className,
  // Add this if you want to fully customize the content styles - useful if you're not just using text content
  custom = false,
  ...rest
}: {
  className?: string;
  custom?: boolean;
} & ComponentProps<typeof Accordion.Content>) => {
  const { variant } = useAccordionContext();

  return (
    <Accordion.Content
      className={cn(!custom && accordionStyles().content({ variant }), className)}
      {...rest}
    >
      {children}
    </Accordion.Content>
  );
};
